export type SharedFamilyMealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export type SharedFamilyShoppingListItem = {
  label: string;
  count: number;
  usedBy: string[];
};

export type SharedFamilyFridgeMealSuggestion = {
  key: string;
  title: string;
  slot: SharedFamilyMealSlot;
  summary: string;
  matchedItems: string[];
  shoppingTopUps: string[];
  ingredients: string[];
  note: string;
};

export type SharedFamilySupermarketLink = {
  id: 'tesco' | 'sainsburys' | 'asda' | 'ocado' | 'waitrose' | 'morrisons';
  label: string;
  providerName: string;
  handoffBody: string;
  url: string;
};

export type SharedFamilySupermarketId = SharedFamilySupermarketLink['id'];

export type SharedFamilyAsdaBasketStep = {
  key: string;
  label: string;
  searchLabel: string;
  url: string;
};

export type SharedFamilyAsdaBasketPlan = {
  title: string;
  body: string;
  steps: SharedFamilyAsdaBasketStep[];
};

export type SharedFamilySuggestedChore = {
  task: string;
  note: string;
};

export type SharedFamilyAdminViewKey = 'trackers' | 'setup';

export const SHARED_FAMILY_ADMIN_VIEWS: Array<{
  key: SharedFamilyAdminViewKey;
  label: string;
  subtitle: string;
}> = [
  {
    key: 'trackers',
    label: 'Track & do',
    subtitle: 'Deadlines and the live family admin board.',
  },
  {
    key: 'setup',
    label: 'Support',
    subtitle: 'Practical guides and quick-start packs.',
  },
];

export type SharedFamilyChildChoreSuggestionGroup<TChild extends { id: string; name: string; age: number }> = {
  child: TChild;
  ageLabel: string;
  suggestions: Array<SharedFamilySuggestedChore & { alreadyAdded: boolean }>;
};

type CanonicalFoodKey =
  | 'egg'
  | 'cheese'
  | 'tomato'
  | 'pasta'
  | 'rice'
  | 'potato'
  | 'bread'
  | 'wrap'
  | 'chicken'
  | 'mince'
  | 'beans'
  | 'tuna'
  | 'salad'
  | 'leafy'
  | 'pepper'
  | 'mushroom'
  | 'onion'
  | 'stock'
  | 'curry'
  | 'coconut'
  | 'yoghurt'
  | 'sausage';

const FOOD_KEY_PATTERNS: Array<{ key: CanonicalFoodKey; patterns: string[] }> = [
  { key: 'egg', patterns: ['egg'] },
  { key: 'cheese', patterns: ['cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'halloumi'] },
  { key: 'tomato', patterns: ['tomato', 'passata', 'marinara'] },
  { key: 'pasta', patterns: ['pasta', 'spaghetti', 'penne', 'fusilli', 'linguine', 'macaroni', 'lasagne'] },
  { key: 'rice', patterns: ['rice', 'grain pouch', 'microwave rice'] },
  { key: 'potato', patterns: ['potato', 'potatoes', 'sweet potato'] },
  { key: 'bread', patterns: ['bread', 'bagel', 'roll', 'toastie', 'toast'] },
  { key: 'wrap', patterns: ['wrap', 'tortilla', 'naan', 'pitta', 'pita'] },
  { key: 'chicken', patterns: ['chicken'] },
  { key: 'mince', patterns: ['mince', 'bolognese beef', 'turkey mince', 'quorn mince'] },
  { key: 'beans', patterns: ['bean', 'beans', 'chickpea', 'chickpeas', 'lentil', 'lentils'] },
  { key: 'tuna', patterns: ['tuna'] },
  { key: 'salad', patterns: ['lettuce', 'cucumber', 'rocket'] },
  { key: 'leafy', patterns: ['spinach', 'kale', 'greens'] },
  { key: 'pepper', patterns: ['pepper', 'peppers'] },
  { key: 'mushroom', patterns: ['mushroom', 'mushrooms'] },
  { key: 'onion', patterns: ['onion', 'shallot', 'spring onion'] },
  { key: 'stock', patterns: ['stock', 'broth'] },
  { key: 'curry', patterns: ['curry paste', 'curry powder', 'korma', 'tikka'] },
  { key: 'coconut', patterns: ['coconut milk'] },
  { key: 'yoghurt', patterns: ['yoghurt', 'yogurt'] },
  { key: 'sausage', patterns: ['sausage', 'sausages'] },
];

function dedupeStrings(values: string[]) {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

export function normalizeFamilyChoreTask(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function getSuggestedFamilyChoresForAge(age: number): SharedFamilySuggestedChore[] {
  if (age <= 2) {
    return [
      { task: 'Help put toys back in the basket', note: 'A gentle start for joining in rather than getting it perfect.' },
      { task: 'Carry dirty clothes to the laundry basket', note: 'Good for building the habit without making it a battle.' },
      { task: 'Bring books back to the shelf', note: 'A simple tidy-up job after stories or play.' },
    ];
  }

  if (age <= 4) {
    return [
      { task: 'Put toys away before bedtime', note: 'Short, visible jobs work best at this age.' },
      { task: 'Put shoes or coat by the door', note: 'Makes the next morning easier for everyone.' },
      { task: 'Help feed the pet with support', note: 'A small responsibility that feels real but still supervised.' },
    ];
  }

  if (age <= 7) {
    return [
      { task: 'Make the bed or pull the duvet up', note: 'A quick everyday task that becomes routine fast.' },
      { task: 'Set the table for dinner', note: 'Useful and repeatable without being too open-ended.' },
      { task: 'Pack school bag with an adult check', note: 'Good for building confidence around school admin.' },
    ];
  }

  if (age <= 10) {
    return [
      { task: 'Unload cutlery or plasticware from the dishwasher', note: 'A practical next step once the basics feel normal.' },
      { task: 'Water plants or refill pet water', note: 'A steady responsibility that is easy to track.' },
      { task: 'Sort socks or simple laundry items', note: 'Small enough to finish, useful enough to matter.' },
    ];
  }

  if (age <= 13) {
    return [
      { task: 'Vacuum or tidy bedroom floor', note: 'A fuller-room reset starts to feel manageable here.' },
      { task: 'Take bins or recycling to the right place', note: 'A solid weekly household job with a clear finish.' },
      { task: 'Prep tomorrow\'s lunch or snack bag', note: 'Builds independence and takes pressure off mornings.' },
    ];
  }

  return [
    { task: 'Run one load of laundry end to end', note: 'A proper household task that grows real self-sufficiency.' },
    { task: 'Cook or help cook one simple meal', note: 'Useful life-admin practice, not just another chore.' },
    { task: 'Clean the bathroom sink and mirror', note: 'A contained cleaning task with a clear standard.' },
  ];
}

export function getFamilyChoreAgeSuggestionLabel(age: number) {
  if (age <= 2) return 'Early helper jobs';
  if (age <= 4) return 'Starter jobs for age 3-4';
  if (age <= 7) return 'Good next jobs for age 5-7';
  if (age <= 10) return 'Good next jobs for age 8-10';
  if (age <= 13) return 'Good next jobs for age 11-13';
  return 'Good next jobs for teens';
}

export function buildFamilyChildChoreSuggestionGroups<TChild extends { id: string; name: string; age: number }>(
  children: TChild[],
  chores: Array<{ task: string; assignedTo: string }>,
) {
  const fingerprints = new Set(
    chores.map((chore) => `${chore.assignedTo.toLowerCase()}::${normalizeFamilyChoreTask(chore.task)}`),
  );

  return children
    .filter((child) => child.name.trim())
    .map((child) => ({
      child,
      ageLabel: getFamilyChoreAgeSuggestionLabel(child.age),
      suggestions: getSuggestedFamilyChoresForAge(child.age).map((suggestion) => ({
        ...suggestion,
        alreadyAdded: fingerprints.has(`${child.name.trim().toLowerCase()}::${normalizeFamilyChoreTask(suggestion.task)}`),
      })),
    }))
    .filter((group) => group.suggestions.length > 0) as SharedFamilyChildChoreSuggestionGroup<TChild>[];
}

function normaliseSupermarketQueryLabel(value: string) {
  return value
    .replace(/\sx\d+$/i, '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normaliseFoodValue(value: string) {
  return value.toLowerCase().replace(/[&/]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function detectCanonicalFoodKey(value: string): CanonicalFoodKey | null {
  const normalised = normaliseFoodValue(value);
  for (const entry of FOOD_KEY_PATTERNS) {
    if (entry.patterns.some((pattern) => normalised.includes(pattern))) {
      return entry.key;
    }
  }
  return null;
}

function uniqueLabelsByKey(visibleItems: string[]) {
  const labelsByKey = new Map<CanonicalFoodKey, string[]>();
  const fallbackLabels: string[] = [];

  visibleItems.forEach((label) => {
    const detectedKey = detectCanonicalFoodKey(label);
    if (!detectedKey) {
      fallbackLabels.push(label);
      return;
    }
    const existing = labelsByKey.get(detectedKey) || [];
    if (!existing.includes(label)) {
      existing.push(label);
      labelsByKey.set(detectedKey, existing);
    }
  });

  return { labelsByKey, fallbackLabels };
}

function buildHouseholdScaleNote(householdSize?: number) {
  if (!householdSize || householdSize <= 1) {
    return 'Good for one person without creating loads of extra admin.';
  }

  if (householdSize === 2) {
    return 'Easy to pull together for two without making the evening heavier.';
  }

  if (householdSize >= 5) {
    return 'Scales well when the household is bigger and dinner needs to stretch.';
  }

  return 'Easy to stretch across the household without a complicated plan.';
}

function createSuggestion(
  key: string,
  title: string,
  slot: SharedFamilyMealSlot,
  matchedItems: string[],
  shoppingTopUps: string[],
  householdSize?: number,
  extraNote?: string,
): SharedFamilyFridgeMealSuggestion {
  const dedupedMatched = dedupeStrings(matchedItems);
  const dedupedTopUps = dedupeStrings(shoppingTopUps);
  const ingredients = dedupeStrings([...dedupedMatched, ...dedupedTopUps]);
  const leadItems = dedupedMatched.slice(0, 4).join(', ');
  const scaleNote = buildHouseholdScaleNote(householdSize);
  const topUpNote = dedupedTopUps.length
    ? ` Top up with ${dedupedTopUps.join(', ')} if the cupboard is light.`
    : '';
  const note = `${scaleNote}${topUpNote}${extraNote ? ` ${extraNote}` : ''}`.trim();

  return {
    key,
    title,
    slot,
    matchedItems: dedupedMatched,
    shoppingTopUps: dedupedTopUps,
    ingredients,
    summary: leadItems
      ? `Uses up ${leadItems}${dedupedTopUps.length ? `, with ${dedupedTopUps.join(', ')} as a top-up if needed` : ''}.`
      : `Builds around what is already visible in the fridge${dedupedTopUps.length ? `, with ${dedupedTopUps.join(', ')} as a top-up if needed` : ''}.`,
    note,
  };
}

export function parseFamilyIngredientList(value: string) {
  return dedupeStrings(
    value
      .split(/\n|,/)
      .map((item) => item.trim()),
  );
}

export function buildFamilyShoppingList(meals: Array<{ title: string; ingredients: string[] }>) {
  const grouped = new Map<string, { label: string; count: number; usedBy: Set<string> }>();

  meals.forEach((meal) => {
    meal.ingredients.forEach((ingredient) => {
      const cleaned = ingredient.trim();
      if (!cleaned) return;
      const key = cleaned.toLowerCase().replace(/\s+/g, ' ');
      const existing = grouped.get(key);
      if (existing) {
        existing.count += 1;
        existing.usedBy.add(meal.title);
        return;
      }
      grouped.set(key, {
        label: cleaned,
        count: 1,
        usedBy: new Set([meal.title]),
      });
    });
  });

  return Array.from(grouped.values())
    .sort((left, right) => left.label.localeCompare(right.label))
    .map((item) => ({
      label: item.label,
      count: item.count,
      usedBy: Array.from(item.usedBy),
    })) satisfies SharedFamilyShoppingListItem[];
}

export function formatFamilyShoppingList(items: SharedFamilyShoppingListItem[]) {
  return items
    .map((item) => `${item.label}${item.count > 1 ? ` x${item.count}` : ''}`)
    .join('\n');
}

export function buildFamilyShoppingListFromText(value: string) {
  return parseFamilyIngredientList(value).map((label) => ({ label }));
}

export function buildFamilyFridgeMealSuggestions(visibleItems: string[], householdSize?: number) {
  const cleanedItems = dedupeStrings(visibleItems);
  if (cleanedItems.length === 0) {
    return [] satisfies SharedFamilyFridgeMealSuggestion[];
  }

  const { labelsByKey, fallbackLabels } = uniqueLabelsByKey(cleanedItems);
  const has = (...keys: CanonicalFoodKey[]) => keys.some((key) => labelsByKey.has(key));
  const pick = (...keys: CanonicalFoodKey[]) => dedupeStrings(keys.flatMap((key) => labelsByKey.get(key) || []));
  const suggestions: SharedFamilyFridgeMealSuggestion[] = [];
  const push = (suggestion: SharedFamilyFridgeMealSuggestion) => {
    if (!suggestions.some((item) => item.key === suggestion.key)) {
      suggestions.push(suggestion);
    }
  };

  if (has('egg') && (has('cheese') || has('tomato') || has('pepper') || has('mushroom') || has('leafy') || has('onion'))) {
    push(createSuggestion(
      'frittata',
      'Use-up frittata',
      'dinner',
      pick('egg', 'cheese', 'tomato', 'pepper', 'mushroom', 'leafy', 'onion'),
      ['salad or toast'],
      householdSize,
    ));
  }

  if (has('pasta') && (has('tomato') || has('cheese') || has('chicken') || has('mushroom') || has('leafy'))) {
    push(createSuggestion(
      'pasta',
      'Quick pasta night',
      'dinner',
      pick('pasta', 'tomato', 'cheese', 'chicken', 'mushroom', 'leafy', 'onion'),
      has('tomato') ? [] : ['passata or tomatoes'],
      householdSize,
    ));
  }

  if (has('rice') && (has('egg') || has('chicken') || has('pepper') || has('onion') || has('leafy'))) {
    push(createSuggestion(
      'fried-rice',
      'Fridge fried rice',
      'dinner',
      pick('rice', 'egg', 'chicken', 'pepper', 'onion', 'leafy'),
      ['soy sauce'],
      householdSize,
    ));
  }

  if (has('potato') && (has('cheese') || has('beans') || has('salad') || has('leafy'))) {
    push(createSuggestion(
      'baked-potatoes',
      'Loaded baked potatoes',
      'dinner',
      pick('potato', 'cheese', 'beans', 'salad', 'leafy', 'onion'),
      ['butter or yoghurt'],
      householdSize,
    ));
  }

  if ((has('bread') || has('wrap')) && (has('cheese') || has('chicken') || has('tuna') || has('tomato') || has('salad'))) {
    push(createSuggestion(
      'wraps-toasties',
      has('wrap') ? 'Wrap night' : 'Toasties or sandwiches',
      'lunch',
      pick('bread', 'wrap', 'cheese', 'chicken', 'tuna', 'tomato', 'salad'),
      ['crisps or fruit'],
      householdSize,
    ));
  }

  if ((has('curry') || has('coconut')) && (has('chicken') || has('beans') || has('pepper') || has('onion') || has('leafy'))) {
    push(createSuggestion(
      'quick-curry',
      'Quick curry',
      'dinner',
      pick('curry', 'coconut', 'chicken', 'beans', 'pepper', 'onion', 'leafy'),
      has('rice') ? [] : ['rice or naan'],
      householdSize,
    ));
  }

  if (has('stock') && (has('potato') || has('tomato') || has('leafy') || has('mushroom') || has('beans') || has('onion'))) {
    push(createSuggestion(
      'soup',
      'Fridge-clear soup',
      'lunch',
      pick('stock', 'potato', 'tomato', 'leafy', 'mushroom', 'beans', 'onion'),
      ['bread'],
      householdSize,
    ));
  }

  if (has('mince') && (has('tomato') || has('beans') || has('onion'))) {
    push(createSuggestion(
      'chilli-bolognese',
      'Chilli or bolognese base',
      'dinner',
      pick('mince', 'tomato', 'beans', 'onion', 'pepper'),
      has('pasta') || has('rice') ? [] : ['pasta or rice'],
      householdSize,
    ));
  }

  if (has('sausage') && (has('potato') || has('pepper') || has('onion') || has('tomato'))) {
    push(createSuggestion(
      'sausage-tray',
      'Sausage traybake',
      'dinner',
      pick('sausage', 'potato', 'pepper', 'onion', 'tomato'),
      ['olive oil'],
      householdSize,
    ));
  }

  if (suggestions.length === 0) {
    const fallbackBase = cleanedItems.slice(0, 4);
    push(createSuggestion(
      'fridge-clear-out',
      'Fridge clear-out bowls',
      'dinner',
      fallbackBase,
      ['rice, pasta, or wraps'],
      householdSize,
      fallbackLabels.length > 0 ? 'Use the photo plus your own judgement to decide what needs using first.' : undefined,
    ));
  }

  return suggestions.slice(0, 4);
}

export function buildFamilySupermarketLinks(items: Array<{ label: string }>) {
  const query = encodeURIComponent(
    dedupeStrings(
      items
        .map((item) => normaliseSupermarketQueryLabel(item.label.trim()))
        .filter(Boolean),
    )
      .slice(0, 8)
      .join(' ') || 'groceries',
  );

  const buildHandoffBody = (providerName: string) =>
    `Adulting copies your full order-ready list and opens ${providerName} with the strongest items already in search. Paste the full list into search, basket notes, or the supermarket app list if you need it exactly as written.`;

  return [
    {
      id: 'tesco',
      label: 'Order with Tesco',
      providerName: 'Tesco groceries',
      handoffBody: buildHandoffBody('Tesco groceries'),
      url: `https://www.tesco.com/groceries/en-GB/search?query=${query}`,
    },
    {
      id: 'sainsburys',
      label: "Order with Sainsbury's",
      providerName: "Sainsbury's groceries",
      handoffBody: buildHandoffBody("Sainsbury's groceries"),
      url: `https://www.sainsburys.co.uk/gol-ui/SearchResults/${query}`,
    },
    {
      id: 'asda',
      label: 'Order with Asda',
      providerName: 'Asda groceries',
      handoffBody: buildHandoffBody('Asda groceries'),
      url: `https://www.asda.com/groceries/search/${query}`,
    },
    {
      id: 'ocado',
      label: 'Order with Ocado',
      providerName: 'Ocado',
      handoffBody: buildHandoffBody('Ocado'),
      url: `https://www.ocado.com/search?entry=${query}`,
    },
    {
      id: 'waitrose',
      label: 'Order with Waitrose',
      providerName: 'Waitrose groceries',
      handoffBody: buildHandoffBody('Waitrose groceries'),
      url: `https://www.waitrose.com/ecom/shop/search?searchTerm=${query}`,
    },
    {
      id: 'morrisons',
      label: 'Order with Morrisons',
      providerName: 'Morrisons groceries',
      handoffBody: buildHandoffBody('Morrisons groceries'),
      url: `https://groceries.morrisons.com/search?entry=${query}`,
    },
  ] satisfies SharedFamilySupermarketLink[];
}

export function buildFamilyAsdaBasketPlan(items: Array<{ label: string }>) {
  const steps = dedupeStrings(
    items
      .map((item) => item.label.trim())
      .filter(Boolean),
  )
    .slice(0, 8)
    .map((label, index) => {
      const searchLabel = normaliseSupermarketQueryLabel(label) || label;

      return {
        key: `asda-${index}-${searchLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        label,
        searchLabel,
        url: `https://www.asda.com/groceries/search/${encodeURIComponent(searchLabel)}`,
      } satisfies SharedFamilyAsdaBasketStep;
    });

  if (steps.length === 0) {
    return null;
  }

  return {
    title: 'Asda basket session',
    body: 'Adulting keeps one Asda session alive so you can sign in once, then move through tighter item searches while the same live basket stays open. The full order-ready list still stays here for anything you want to paste exactly as written.',
    steps,
  } satisfies SharedFamilyAsdaBasketPlan;
}

export function movePreferredFamilySupermarketFirst(
  links: SharedFamilySupermarketLink[],
  preferredId: SharedFamilySupermarketId | null | undefined,
) {
  if (!preferredId) {
    return links;
  }

  return [...links].sort((left, right) => {
    if (left.id === preferredId) return -1;
    if (right.id === preferredId) return 1;
    return 0;
  });
}

export function getPreferredFamilySupermarketLink(
  links: SharedFamilySupermarketLink[],
  preferredId: SharedFamilySupermarketId | null | undefined,
) {
  if (!preferredId) {
    return links[0] || null;
  }

  return links.find((link) => link.id === preferredId) || links[0] || null;
}
